import { useCallback } from 'react';
import apiClient from '../axiosClient';
import {
  GetBrandRelationshipsApiParams,
  GetVerticalsApiParams,
  GetUseCasesApiParams,
  GetEntityTypesApiParams,
  SearchCampaignsByBrandApiParams,
  CreateBrandParams,
  CreateCampaignParams,
  CreateBrandRequest,
  CreateCampaignRequest,
  BrandResponse,
  CampaignResponse,
  CampaignListResponse,
  Brand,
  BrandExternalVetting,
  EnumOption,
  RequestBrandExternalVettingRequest,
  UseCaseOption,
  UseCasesResponse,
  CampaignWithErrorResponse,
  SearchBrandExternalVettingApiParams,
  RequestBrandExternalVettingApiParams,
  VettingOption,
  GetVettingOptionsApiParams,
  SearchBrandApiParams,
} from './types';

// Export types for use in components
export type {
  EnumOption,
  CreateBrandParams,
  CreateCampaignParams,
  CreateBrandRequest,
  CreateCampaignRequest,
  BrandResponse,
  CampaignResponse,
  Brand,
  BrandExternalVetting,
  RequestBrandExternalVettingRequest,
  UseCaseOption,
  UseCasesResponse,
  SearchCampaignsByBrandApiParams,
  CampaignWithErrorResponse,
  SearchBrandExternalVettingApiParams,
  RequestBrandExternalVettingApiParams,
  VettingOption,
  GetVettingOptionsApiParams,
};

/**
 * @function useGetBrandApi
 * @returns { (brandId: string) => Promise<{ data: Brand }> }
 */
export function useGetBrandApi(): (brandId: string) => Promise<{ data: Brand }> {
  return useCallback(brandId => apiClient.get(`/campaigns/registry/brands/${brandId}`), []);
}

/**
 * @function useSearchBrandApi
 * @returns { SearchBrandApiParams }
 */
export function useSearchBrandApi(): SearchBrandApiParams {
  return useCallback(params => apiClient.get(`/campaigns/registry/brands`, { params }), []);
}

/**
 * @function useCreateBrandApi
 * @returns { (params: CreateBrandRequest) => Promise<{ data: Brand }> }
 */
export function useCreateBrandApi(): (params: CreateBrandRequest) => Promise<{ data: Brand }> {
  return useCallback(params => apiClient.post(`/campaigns/registry/brands`, params), []);
}

/**
 * @function useSearchBrandExternalVettingApi
 * @returns { (brandId: string) => Promise<{ data: BrandExternalVetting[] }> }
 */
export function useSearchBrandExternalVettingApi(): SearchBrandExternalVettingApiParams {
  return useCallback(
    brandId => apiClient.get(`/campaigns/registry/brands/${brandId}/external-vetting`),
    [],
  );
}

/**
 * @function useRequestBrandExternalVettingApi
 * @returns { (brandId: string, params: RequestBrandExternalVettingRequest) => Promise<{ data: BrandExternalVetting }> }
 */
export function useRequestBrandExternalVettingApi(): RequestBrandExternalVettingApiParams {
  return useCallback(
    (brandId, params: RequestBrandExternalVettingRequest) =>
      apiClient.post(`/campaigns/registry/brands/${brandId}/external-vetting`, params),
    [],
  );
}

/**
 * @function useGetCampaignApi
 * @returns { (companyId: string) => Promise<{ data: CampaignWithErrorResponse }> }
 */
export function useGetCampaignApi(): (companyId: string) => Promise<{ data: CampaignWithErrorResponse }> {
  return useCallback(
    companyId => apiClient.get(`/campaigns/registry/campaign`, { params: { companyId } }),
    [],
  );
}

/**
 * @function useSearchCampaignsByBrandApi
 * @returns { (brandId: string) => Promise<{ data: CampaignListResponse }> }
 */
export function useSearchCampaignsByBrandApi(): (brandId: string) => Promise<{ data: CampaignListResponse }> {
  return useCallback(
    brandId => apiClient.get(`/campaigns/registry/campaigns`, { params: { brandId } }),
    [],
  );
}

/**
 * @function useCreateCampaignApi
 * @returns { (params: CreateCampaignRequest) => Promise<{ data: CampaignResponse }> }
 */
export function useCreateCampaignApi(): (
  params: CreateCampaignRequest,
) => Promise<{ data: CampaignResponse }> {
  return useCallback(params => apiClient.post(`/campaigns/registry/campaigns`, params), []);
}

/**
 * @function useGetBrandRelationshipsApi
 * @returns { GetBrandRelationshipsApiParams }
 */
export function useGetBrandRelationshipsApi(): GetBrandRelationshipsApiParams {
  return useCallback(() => apiClient.get(`/campaigns/registry/enum/brand-relationship`), []);
}

/**
 * @function useGetVerticalsApi
 * @returns { GetVerticalsApiParams }
 */
export function useGetVerticalsApi(): GetVerticalsApiParams {
  return useCallback(() => apiClient.get(`/campaigns/registry/enum/vertical`), []);
}

/**
 * @function useGetUseCasesApi
 * @returns { GetUseCasesApiParams }
 */
export function useGetUseCasesApi(): GetUseCasesApiParams {
  return useCallback(() => apiClient.get(`/campaigns/registry/enum/usecase`), []);
}

/**
 * @function useGetEntityTypesApi
 * @returns { GetEntityTypesApiParams }
 */
export function useGetEntityTypesApi(): GetEntityTypesApiParams {
  return useCallback(() => apiClient.get(`/campaigns/registry/enum/entity-type`), []);
}

/**
 * @function useGetVettingOptionsApi
 * @returns { GetVettingOptionsApiParams }
 */
export function useGetVettingOptionsApi(): GetVettingOptionsApiParams {
  return useCallback(() => apiClient.get(`/campaigns/registry/enum/vetting-options`), []);
}

/**
 * @function useGetCampaignFlyerApi
 * @returns { (companyId: string) => Promise<{ data: string }> }
 */
export function useGetCampaignFlyerApi(): (companyId: string) => Promise<{ data: { url: string } }> {
  return useCallback(companyId => apiClient.get(`/campaigns/registry/flyer`, { params: { companyId } }), []);
}
